import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { InsertDriveFile, Delete, QrCode, CalendarToday, CloudUpload } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../config/api';
import { QRCodeCanvas } from 'qrcode.react';
import { Helmet } from 'react-helmet';

// Tab Panel bileşeni
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Upload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [event, setEvent] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [tabValue, setTabValue] = useState(event?.image_data ? 1 : 0); // Karşılama ekranı varsayılan
  const { codeHash } = useParams();
  const [maxFileSizeMB, setMaxFileSizeMB] = useState(null); // Plan limiti
  const [maxFileCount, setMaxFileCount] = useState(null); // Maksimum dosya limiti

  // Tab değişim fonksiyonu
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.getByCodeInfo(codeHash), {
        method: 'GET',
      });
      const data = await response.json();
      console.log(data);
      if (response.ok) {
        setEvent(data);
      }
    } catch (error) {
      console.error('Etkinlik bilgileri alınamadı:', error);
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.getUploads(codeHash), {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Dosyalar alınamadı');
      }

      const data = await response.json();
      setUploadedFiles(data);
    } catch (error) {
      console.error('Yüklenen dosyalar alınamadı:', error);
      toast.error('Yüklenen dosyalar alınamadı');
    }
  };

  // Plan limiti API'den çekilecek fonksiyon
  const fetchFileLimit = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.plans.planFileLimit(codeHash), {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // features array içinden ilgili limitleri bul
        if (data.features && Array.isArray(data.features)) {
          const fileSizeFeature = data.features.find(f => f.feature_key === 'dosya_boyutu');
          const maxFileCountFeature = data.features.find(f => f.feature_key === 'maksimum_dosya');
          setMaxFileSizeMB(fileSizeFeature ? parseInt(fileSizeFeature.feature_value) : null);
          setMaxFileCount(maxFileCountFeature ? parseInt(maxFileCountFeature.feature_value) : null);
        } else {
          setMaxFileSizeMB(50);
          setMaxFileCount(5);
        }
      }
    } catch (e) {
      setMaxFileSizeMB(50);
      setMaxFileCount(5);
    }
  }, [codeHash]);

  useEffect(() => {
    fetchEventDetails();
    fetchFileLimit();
  }, [codeHash, fetchFileLimit]);
  useEffect(() => {
    if (event) {
      setTabValue(event.image_data ? 0 : 0);
    }
  }, [event]);

  const onDrop = async (acceptedFiles) => {
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    acceptedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setProgress(percentComplete);
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          toast.success('Dosyalar başarıyla yüklendi!');
          //fetchUploads();
        } else {
          let errorMessage = 'Dosya yüklenirken bir hata oluştu';
          try {
            const errorData = JSON.parse(xhr.responseText);
            if (errorData.error) {
              // Backend'den gelen hata mesajını kontrol et
              if (errorData.error.includes('en az bir dosya seçin')) {
                errorMessage = 'Dosya limiti aşıldı veya geçersiz dosya seçildi. Lütfen daha küçük veya daha az dosya seçin.';
              } else {
                errorMessage = errorData.error;
              }
            }
          } catch (_) { }
          throw new Error(errorMessage);
        }
      };

      xhr.onerror = () => {
        throw new Error('Ağ hatası, dosya yüklenemedi');
      };

      xhr.open('POST', API_ENDPOINTS.events.upload(codeHash), true);
      xhr.withCredentials = true;
      xhr.send(formData);

      await new Promise((resolve, reject) => {
        xhr.onloadend = resolve;
        xhr.onerror = reject;
      });

    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(API_ENDPOINTS.events.upload(codeHash, fileId), {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Dosya başarıyla silindi');
        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Dosya silinirken bir hata oluştu');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles, fileRejections) => {
      // Dosya limiti kontrolü
      if (maxFileSizeMB && maxFileSizeMB > 0) {
        const overLimit = acceptedFiles.find(f => f.size > maxFileSizeMB * 1024 * 1024);
        if (overLimit) {
          toast.error(`Seçilen dosya boyutu plan limitini aşıyor. Maksimum: ${maxFileSizeMB} MB`);
          return;
        }
      }
      if (maxFileCount && maxFileCount > 0) {
        // Yüklenmiş dosya sayısı + yeni seçilen dosya sayısı kontrolü (uploadedFiles state'i kullanılabilir)
        if (uploadedFiles.length + acceptedFiles.length > maxFileCount) {
          toast.error(`Toplam yüklenebilecek dosya sayısı plan limitini aşıyor. Maksimum: ${maxFileCount}`);
          return;
        }
      }
      if (!acceptedFiles.length) {
        toast.error('Yüklemek için geçerli dosya seçmediniz.');
        return;
      }
      await onDrop(acceptedFiles);
    },
    accept: '*/*',
    disabled: uploading
  });

  // QR kod URL'si
  const qrCodeUrl = window.location.href;

  return (
    <Container disableGutters maxWidth="md" sx={{ padding: '0 !important' }}>
      <Helmet>
        <title>{event ? `${event.name} - ${event.event_type_name || 'Etkinlik'}` : 'Etkinlik Dosya Yükleme Sayfası'}</title>
        {event && event.image_data && (
          <meta property="og:image" content={event.image_data} />
        )}
      </Helmet>
      <Box sx={{ mt: 0, mb: 0 }}>
        {event ? (
          <Box sx={{ height: '100vh', overflow: 'hidden', p: 0 }}>
            <Box sx={{
              width: '100%',
              top: 0,
              zIndex: 1000,
              bgcolor: 'background.paper',
              boxShadow: 1
            }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="basic tabs example"
                centered
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: { xs: 'space-between', md: 'center' }
                  },
                  '& .MuiTab-root': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    minWidth: { xs: 'auto', sm: 160 },
                    px: { xs: 1, sm: 2 },
                    minHeight: { xs: 48, sm: 56 }
                  }
                }}
              >
                {event && event.image_data && (
                  <Tab
                    label="Karşılama Ekranı"
                    sx={{
                      flex: { xs: 1, sm: 'none' }
                    }}
                  />
                )}
                <Tab
                  label="Dosya Yükleme"
                  sx={{
                    flex: { xs: 1, sm: 'none' }
                  }}
                />
                <Tab
                  label="Nasıl Yapılır?"
                  sx={{
                    flex: { xs: 1, sm: 'none' }
                  }}
                />
                <Tab
                  label="Anasayfa"
                  component={Link}
                  to="/"
                  sx={{
                    flex: { xs: 1, sm: 'none' }
                  }}
                />
              </Tabs>
            </Box>
            {event && event.image_data && (
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ m: 0, p: 0, margin: '-24px' }}>
                  <div style={{ position: 'relative', height: '94vh', overflow: 'hidden' }}>
                    {/* Üst karartma ve yazılar */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '18%',
                      background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent)',
                      color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      paddingBottom: '10px',
                      paddingTop: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                        {event.event_date ? new Date(event.event_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                      </div>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => setTabValue(1)}
                        sx={{
                          mt: 2,
                          fontSize: '18px',
                          padding: '10px 30px',
                          borderRadius: '25px'
                        }}
                      >
                        Dosyaları Yükle
                      </Button>
                    </div>
                    <img
                      src={event.image_data}
                      alt="Etkinlik görseli önizleme"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />

                    {/* Alt karartma ve yazılar */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to top, rgba(0.9, 0.7, 0.8, 0.9), transparent)',
                      color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      paddingBottom: '40px',
                      paddingTop: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>{event.name || ''}</div>
                      <div style={{ fontSize: '16px', opacity: 0.9, color: "white", padding: "10px", marginBottom: '20px' }}>
                        {event.description || ''}
                      </div>
                    </div>
                  </div>
                </Box>
              </TabPanel>
            )}

            <TabPanel value={tabValue} index={event?.image_data ? 1 : 0}>
              <Box sx={{
                p: 3,
                margin: '-24px',
                height: '100vh',
                overflowY: 'auto',
                pb: 10 // Alt kısımda boşluk bırakmak için padding
              }}>
                <Box sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ color: 'primary.main' }}>
                    {event.name}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'text.secondary'
                    }}>
                      <CalendarToday fontSize="small" />
                      <Typography variant="body2">
                        {event.event_date ? new Date(event.event_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {event.description}
                  </Typography>
                </Box>

                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <input {...getInputProps()} />
                  {uploading ? (
                    <Box sx={{ width: '100%', maxWidth: '400px' }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4
                          }
                        }}
                      />
                      <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                        Yükleniyor... {progress}%
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom color="text.primary">
                        {isDragActive ? 'Dosyaları Buraya Bırakın' : 'Dosyaları Yüklemek İçin Tıklayın'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        veya dosyaları sürükleyip buraya bırakın
                      </Typography>
                    </>
                  )}
                </Box>
                

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Maksimum dosya boyutu: {maxFileSizeMB === 0 ? 'Sınırsız' : maxFileSizeMB ? `${maxFileSizeMB} MB` : 'Bilinmiyor'}<br />
                    Maksimum dosya adedi: {maxFileCount === 0 ? 'Sınırsız' : maxFileCount ? `${maxFileCount} adet` : 'Bilinmiyor'}
                  </Typography>
                </Box>

                {uploadedFiles.length > 0 && (
                  <Box sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                      Yüklenen Dosyalar
                    </Typography>
                    <List sx={{
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}>
                      {uploadedFiles.map(file => (
                        <ListItem
                          key={file.id}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none'
                            },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteFile(file.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            <InsertDriveFile sx={{ color: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={file.file_name}
                            secondary={`${(file.file_size / 1024 / 1024).toFixed(2)} MB`}
                            primaryTypographyProps={{
                              sx: { color: 'text.primary', fontWeight: 500 }
                            }}
                            secondaryTypographyProps={{
                              sx: { color: 'text.secondary' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={event?.image_data ? 2 : 1}>
              <Typography variant="h5" component="h2" gutterBottom>
                Nasıl Dosya Yükleyebilirim?
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="1. QR Kodu Tarayın"
                    secondary="Yukarıdaki QR kodu mobil cihazınızla tarayarak bu sayfaya erişebilirsiniz."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="2. Dosyaları Seçin"
                    secondary="'Dosya Yükleme' sekmesine geçin ve dosyalarınızı sürükleyip bırakın veya tıklayarak seçin."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="3. Yüklemeyi Bekleyin"
                    secondary="Seçtiğiniz dosyalar otomatik olarak yüklenmeye başlayacaktır. Yükleme durumunu ilerleme çubuğundan takip edebilirsiniz."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="4. Dosyalarınızı Yönetin"
                    secondary="Yüklenen dosyaları listeden görebilir ve gerektiğinde silebilirsiniz."
                  />
                </ListItem>
              </List>
            </TabPanel>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Typography>ETKİNLİK KAPATILMIŞ OLABİLİR. DAVET SAHİBİ İLE İLETİŞİME GEÇİNİZ.</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Upload;
